import type { HeroClass, SkillId } from './heroes.ts';
import type { DamageType } from './damage.ts';

/**
 * Skill trees (SPEC §6), reworked 2026-10-27 (docs/PODERES.md): one branch per slot plus a bastion
 * branch, one point per level from level 2. A point learns a skill of that slot (from its level) or a
 * variant of a known skill; ranks and stats come with the level. What the hero carries into each fight
 * is chosen apart, for free (a preparation).
 */

export type HeroStatKey = 'damage' | 'hp' | 'attackSpeed' | 'cooldown' | 'armor' | 'range' | 'healing';

export type BastionPerk = 'repairDiscount' | 'workBonus' | 'ghostPower' | 'incursionLoot';

/** What a fork node does to its skill (numbers in `FORKS`). */
export type ForkId =
  | 'bashCharge'
  | 'bashCleave'
  | 'ironWall'
  | 'warCry'
  | 'sharedOath'
  | 'secondWind'
  | 'frostTurret'
  | 'fireTurret'
  | 'shrapnel'
  | 'magnet'
  | 'colossus'
  | 'swarm'
  | 'meteor'
  | 'embers'
  | 'longWall'
  | 'bitingFrost'
  | 'stillStorm'
  | 'thunderclap'
  | 'volley'
  | 'frostArrows'
  | 'wideNet'
  | 'steelNet'
  | 'pinningRain'
  | 'wideRain'
  | 'mendChain'
  | 'mendWard'
  | 'consecrate'
  | 'bulwark'
  | 'dawnRebirth'
  | 'dawnSmite'
  | 'whirlwind'
  | 'rend'
  | 'quake'
  | 'doubleLeap'
  | 'bloodlust'
  | 'warpath'
  | 'judgment'
  | 'healingLight'
  | 'reprisal'
  | 'faithWall'
  | 'holyGround'
  | 'faithWeight'
  | 'strangle'
  | 'spores'
  | 'deepGrove'
  | 'briars'
  | 'direBear'
  | 'wildHeart'
  | 'spikedDecoy'
  | 'twinDecoy'
  | 'orbBurst'
  | 'deepOrb'
  | 'talons'
  | 'hawkEye'
  | 'mendingTotem'
  | 'lightTotem'
  | 'stunHammer'
  | 'wideHammer'
  | 'dreadRoar'
  | 'longRoar'
  | 'welderDrone'
  | 'armorDrone'
  | 'jumpingWasps'
  | 'denseWasps'
  | 'crippleBlow'
  | 'exposeBlow'
  | 'longArc'
  | 'overcharge'
  | 'piercingVenom'
  | 'virulentArrow'
  | 'brightRay'
  | 'mendingRay'
  | 'bleedingAxe'
  | 'cleavingAxe'
  | 'searingLight'
  | 'wideLight'
  | 'clusterMine'
  | 'tarMine'
  | 'longCharge'
  | 'crushingCharge'
  | 'barkskin'
  | 'spreadingRoots'
  | 'rallyBanner'
  | 'wideBanner'
  | 'whiteout'
  | 'deepFreeze'
  | 'pinningArrow'
  | 'burstingArrow'
  | 'mercifulDawn'
  | 'blindingDawn'
  | 'sacredRampart'
  | 'longRampart'
  | 'heavyMortar'
  | 'incendiaryMortar'
  | 'virulentPlague'
  | 'witheringPlague'
  | 'heavyChain'
  | 'twinChain'
  | 'deepRepair'
  | 'wideRepair'
  | 'longLance'
  | 'bitingLance'
  | 'heavyShot'
  | 'pinShot'
  | 'longRenew'
  | 'sharedRenew'
  | 'executeBlow'
  | 'stunBlow'
  | 'brightSeal'
  | 'burningSeal'
  | 'longLash'
  | 'deepThorns'
  | 'wideShout'
  | 'longShout'
  | 'bigCoil'
  | 'fastCoil'
  | 'frostBlink'
  | 'farBlink'
  | 'barbedSnare'
  | 'twinSnare'
  | 'deepPrayer'
  | 'wardPrayer'
  | 'longLetting'
  | 'thirstyLetting'
  | 'steadfast'
  | 'healingStep'
  | 'longThorns'
  | 'sharpThorns'
  | 'sharedStand'
  | 'longStand'
  | 'ironHound'
  | 'packHound'
  | 'emberStorm'
  | 'greatMeteors'
  | 'longEye'
  | 'keenEye'
  | 'longIntercession'
  | 'fullIntercession'
  | 'longFury'
  | 'wideFury'
  | 'radiantAvatar'
  | 'guardianAvatar'
  | 'ancientTreant'
  | 'threeTreants'
  | 'wideSlam'
  | 'hardSlam'
  | 'bulwarkWall'
  | 'wideWall'
  | 'thunderEcho'
  | 'longEcho'
  | 'longFlame'
  | 'napalm'
  | 'longBarricade'
  | 'spikedBarricade'
  | 'reinforce'
  | 'longFortify'
  | 'corrosiveDart'
  | 'splashDart'
  | 'widerChains'
  | 'heavyChains'
  | 'deepRift'
  | 'wideRift'
  | 'farLeap'
  | 'sharpCaltrops'
  | 'brightFlare'
  | 'burningFlare'
  | 'longHidden'
  | 'deadlyHidden'
  | 'brightLance'
  | 'mercyLight'
  | 'wideCleanse'
  | 'wardCleanse'
  | 'longChoir'
  | 'loudChoir'
  | 'farKick'
  | 'bruteKick'
  | 'wideTerrify'
  | 'deepTerrify'
  | 'longDuel'
  | 'bloodDuel'
  | 'greaterAtone'
  | 'sharedAtone'
  | 'longBlessing'
  | 'brightBlessing'
  | 'longSacrifice'
  | 'wardedSacrifice'
  | 'thickSpores'
  | 'venomSpores'
  | 'longWolf'
  | 'swiftWolf'
  | 'longQuake'
  | 'shatterQuake';

export type NodeEffect =
  /** Teaches another skill for that slot, from `level` on (docs/PODERES.md §2); a tome's comes with its pages, not points (§6). */
  | { kind: 'learn'; skill: SkillId; level: number; tome?: true }
  /** A variant of a skill: changes how it works while carried. Both of a skill can be known; one is carried at a time. */
  | { kind: 'variant'; skill: SkillId; fork: ForkId }
  /** Bastion perks (applied by the campaign layer). */
  | { kind: 'bastion'; perk: BastionPerk; bpPerRank: number };

export interface SkillNode {
  id: string;
  cls: HeroClass;
  /** 0..2: the branch of that slot (short, medium, ultimate); 3: the bastion. */
  branch: 0 | 1 | 2 | 3;
  /** Position in the branch; a bastion node needs at least one rank in the one before it. */
  tier: number;
  maxRank: 1 | 2 | 3;
  effect: NodeEffect;
}

/** Power per skill rank: ranks II and III add this much each (damage, duration or amount). */
export const SKILL_RANK_BP = 3000;
/** The levels that bring ranks II and III, to whatever skill is carried in each slot. */
export const SKILL_RANK_LEVELS = [7, 16] as const;

/**
 * What the old tree gave in stats (bp, with every node taken) now comes with the level, on top of the
 * level growth: little at first, all of it at `LEVEL_STATS_FULL_AT` (the curve is in the sim's `levelStat`).
 */
export const LEVEL_STATS: Record<HeroClass, Partial<Record<HeroStatKey, number>>> = {
  warrior: { damage: 1200, attackSpeed: 1000, hp: 3600, armor: 1000, cooldown: 1200 },
  artificer: { damage: 1200, range: 1200, hp: 1600, armor: 1000, cooldown: 1200, attackSpeed: 1400 },
  mage: { damage: 1400, range: 1000, hp: 1800, armor: 1200, cooldown: 1400, attackSpeed: 1600 },
  ranger: { attackSpeed: 1200, damage: 1200, hp: 1800, armor: 1200, cooldown: 1400, range: 1200 },
  cleric: { cooldown: 2600, range: 1000, hp: 1800, armor: 1200, healing: 1400 },
  barbarian: { damage: 1400, attackSpeed: 1200, hp: 3800, cooldown: 2600 },
  paladin: { damage: 1200, attackSpeed: 1000, hp: 3600, armor: 1000, cooldown: 1400 },
  druid: { cooldown: 2600, range: 1000, hp: 1800, armor: 1200, healing: 1400 },
};
export const LEVEL_STATS_FULL_AT = 30;

/**
 * What a hero carries into a fight (docs/PODERES.md §3): a few named preparations, each one skill and
 * variant per slot; every kind of fight remembers the last one used there.
 */
export type CarryMode = 'wave' | 'crypt' | 'incursion' | 'expedition' | 'saga';
export const CARRY_MODES: readonly CarryMode[] = ['wave', 'crypt', 'incursion', 'expedition', 'saga'];
export const LOADOUTS = { count: 3, nameMax: 16 };

/** Heroes a player keeps in one campaign: one plays, the rest wait on the bench (docs/DECISIONS.md 2026-10-29). */
export const ROSTER = { max: 3, nameMax: 20 };

/** Starting over: free once after each level-up, otherwise dream dust per point spent. */
export const RESPEC = { dustPerPoint: 15 };

/**
 * Fork numbers. Bp values multiply (10000 = ×1); `slowBp` is how much slower; ticks are at 12 Hz.
 */
export const FORKS = {
  /** Shield bash from afar: the warrior leaps to the target. */
  bashCharge: { castRange: 4000 },
  /** The bash also hits the nearest others around the target, for part of the damage. */
  bashCleave: { extra: 2, radius: 1500, damageBp: 6000 },
  /** Damage taken while the challenge lasts. */
  ironWall: { takenBp: 6500 },
  /** Challenged nightmares are slowed while taunted. */
  warCry: { slowBp: 4000 },
  /** Allies in the oath's radius are also untouchable, for part of it. */
  sharedOath: { durationBp: 5000 },
  /** The oath heals the warrior. */
  secondWind: { healBp: 3500 },
  /** Turret bullets carry frost or fire (the enchant effects of `ENCHANT_FX`), and so does an overload: frost slows, fire leaves the ground burning. */
  frostTurret: { slowBp: 5000, slowTicks: 36 },
  fireTurret: { ticks: 36, every: 6, damage: 8 },
  /** The grenade bursts harder and its shards slow whoever they hit. */
  shrapnel: { damageBp: 15000, slowBp: 5000, slowTicks: 36 },
  /** A wider grenade that drags for longer. */
  magnet: { radiusBp: 15000, durationBp: 15000 },
  /** One huge automaton: tougher, draws from farther away (and holds a boss longer), hits harder. */
  colossus: { hpBp: 20000, tauntBp: 14000, damageBp: 13000, bossBp: 15000 },
  /** Instead of one automaton, a swarm of spider-bots that run at the nightmares and blow up. */
  swarm: { count: 3, hp: 80, step: 210, damage: 70, radius: 1200, reach: 450, hunt: 6000, ticks: 144 },
  /** Bigger, slower fireball. */
  meteor: { damageBp: 16000, radiusBp: 14000, cooldownBp: 15000 },
  /** The fireball leaves the ground burning. */
  embers: { ticks: 36, every: 6, damage: 7 },
  /** A frost wall three hexes longer. */
  longWall: { extra: 3 },
  /** The frost wall bites whoever stands in it. */
  bitingFrost: { every: 6, damage: 6 },
  /** Nightmares inside the storm are slowed. */
  stillStorm: { slowBp: 4000 },
  /** The storm strikes all at once, harder. */
  thunderclap: { damageBp: 13000 },
  /** More arrows, a little weaker each. */
  volley: { extra: 3, damageBp: 8000 },
  /** Arrows carry frost. */
  frostArrows: {},
  /** The net catches everyone around the target. */
  wideNet: { radius: 1200, max: 6 },
  /** A longer net that also holds warlords, for half. */
  steelNet: { stunBp: 17500 },
  /** Nightmares under the rain are slowed. */
  pinningRain: { slowBp: 4000 },
  /** A rain half again as wide. */
  wideRain: { radiusBp: 15000 },
  /** The mend jumps on to the most hurt heroes near the target, for part of it. */
  mendChain: { extra: 2, radius: 3500, amountBp: 5500 },
  /** The mended hero is warded: takes less damage for a while. */
  mendWard: { takenBp: 6500, ticks: 48 },
  /** The sanctuary burns nightmares standing on it, every pulse. */
  consecrate: { damage: 12 },
  /** Heroes on the sanctuary take less damage. */
  bulwark: { takenBp: 7000 },
  /** Dawn brings the fallen back whole, and untouchable for longer. */
  dawnRebirth: { invulnBp: 20000 },
  /** Dawn strikes every nightmare around with light. */
  dawnSmite: { damage: 160 },
  /** The barbarian spins on as it walks: `hits` more blows of `damageBp`, one every `every` ticks. */
  whirlwind: { hits: 4, every: 6, damageBp: 6000 },
  /** The whirl leaves them bleeding. */
  rend: { damage: 12, every: 6, ticks: 36 },
  /** A wider landing that slows whoever it doesn't stun. */
  quake: { radiusBp: 15000, slowBp: 4000, slowTicks: 24 },
  /** Two leaps in a row: the second is ready this soon after the first. */
  doubleLeap: { gap: 12 },
  /** Each nightmare it kills in the frenzy draws it out, up to a limit. */
  bloodlust: { perKill: 12, max: 72 },
  /** Allies this close attack faster while the frenzy lasts. */
  warpath: { radius: 3500, attackSpeedBp: 12000 },
  /** The smite leaps on to the nearest corrupt or elite nightmares around, for part of it. */
  judgment: { extra: 2, radius: 2500, damageBp: 6000 },
  /** The paladin and the allies around heal part of the smite's damage. */
  healingLight: { radius: 2000, healBp: 5000 },
  /** What the aegis soaks goes back to whoever struck. */
  reprisal: {},
  /** The aegis also covers, for half, the allies and structures around its target. */
  faithWall: { radius: 1500, amountBp: 5000 },
  /** Wider, longer hallowed ground. */
  holyGround: { radiusBp: 14000, durationBp: 15000 },
  /** Nightmares on hallowed ground are slowed. */
  faithWeight: { slowBp: 4000 },
  /** The roots squeeze: more damage while they hold. */
  strangle: { damageBp: 30000, every: 6 },
  /** When the roots let go they burst into a cloud that slows. */
  spores: { slowBp: 4000, ticks: 36 },
  /** A bigger bear that draws the nightmares around to it. */
  direBear: { taunt: 3000, hpBp: 12000 },
  /** Each of the bear's blows heals it and the allies around for part of the damage. */
  wildHeart: { radius: 1500, healBp: 2500 },
  /** A wider grove that lasts longer. */
  deepGrove: { radiusBp: 14000, durationBp: 15000 },
  /** Briars in the grove: the nightmares in it are slowed and pricked every pulse. */
  briars: { slowBp: 3000, damage: 8 },
  /** Whoever strikes the decoy is hurt back by part of the blow. */
  spikedDecoy: { reflectBp: 5000 },
  /** Two decoys side by side, each with part of the health. */
  twinDecoy: { hpBp: 6000, gap: 900 },
  /** When the orb melts it bursts, hurting and freezing everything around. */
  orbBurst: { damage: 60, radius: 1800, stun: 12 },
  /** A slower orb (it lasts twice as long) that slows harder. */
  deepOrb: { stepBp: 5000, slowBp: 7000 },
  /** The hawk's talons leave its prey bleeding. */
  talons: { ticks: 36, every: 6, damage: 4 },
  /** A sharper eye: a heavier mark, and stalkers near the hawk can be seen. */
  hawkEye: { markBp: 13000, reveal: 2500 },
  /** The totem also mends the allies around it (bp of their life each pulse). */
  mendingTotem: { every: 12, hpBp: 150 },
  /** The totem strikes the nearest nightmare in reach with light each pulse. */
  lightTotem: { every: 12, damage: 20 },
  /** Every third pulse the hammer stuns what it strikes. */
  stunHammer: { pulses: 3, stun: 9 },
  /** A wider whirl. */
  wideHammer: { radiusBp: 15000 },
  /** The roar freezes the nightmares around with dread. */
  dreadRoar: { radius: 2500, stun: 18 },
  /** A roar that lasts half again as long. */
  longRoar: { durationBp: 15000 },
  /** The drone also sparks at the nearest nightmare each pulse. */
  welderDrone: { radius: 1800, damage: 18 },
  /** Structures near the drone take less while it's there. */
  armorDrone: { radius: 1500, takenBp: 7000 },
  /** When their nightmare falls, the wasps fly to the nearest one with what's left of their sting. */
  jumpingWasps: { radius: 2500 },
  /** A thicker cloud that stings the target and the two nearest to it. */
  denseWasps: { radius: 1500, extra: 2 },
  /** The low blow also slows. */
  crippleBlow: { slowBp: 5000, slowTicks: 24 },
  /** A deeper mark. */
  exposeBlow: { markBp: 14000 },
  /** More jumps, each weaker. */
  longArc: { jumps: 7, fallBp: 7500 },
  /** Fewer jumps; the last one bursts around. */
  overcharge: { jumps: 3, radius: 1500, damage: 40 },
  /** The arrow goes through: everything in its line is poisoned. */
  piercingVenom: { width: 600 },
  /** A stronger, longer poison. */
  virulentArrow: { damageBp: 15000, durationBp: 15000 },
  /** A wider ray. */
  brightRay: { widthBp: 16000 },
  /** A ray that heals more. */
  mendingRay: { healBp: 16000 },
  /** The axe leaves its target bleeding. */
  bleedingAxe: { ticks: 36, every: 6, damage: 5 },
  /** Coming back, the axe also cuts the two nearest to its target. */
  cleavingAxe: { extra: 2, radius: 1500, damageBp: 6000 },
  /** The light also burns. */
  searingLight: { damage: 30 },
  /** A wider, longer cone. */
  wideLight: { radiusBp: 14000, coneDeg: 130 },
  /** Three smaller mines in a triangle. */
  clusterMine: { count: 3, damageBp: 6000, gap: 900 },
  /** A tar mine: a longer, harder slow. */
  tarMine: { slowBp: 7000, slowTicks: 60 },
  /** A longer run. */
  longCharge: { castRange: 7500 },
  /** Harder blows, a longer stun. */
  crushingCharge: { damageBp: 15000, stunBp: 15000 },
  /** The rooted ally also takes less. */
  barkskin: { takenBp: 7000 },
  /** The roots spread to the allies around the target. */
  spreadingRoots: { radius: 1500 },
  /** The banner also mends the allies by it (bp of their life each pulse). */
  rallyBanner: { hpBp: 200 },
  /** A taller banner: wider. */
  wideBanner: { radiusBp: 15000 },
  /** A wider blizzard. */
  whiteout: { radiusBp: 14000 },
  /** Every fourth pulse the blizzard freezes everything under it a moment. */
  deepFreeze: { pulses: 4, stun: 12 },
  /** What the arrow goes through is pinned. */
  pinningArrow: { stun: 24 },
  /** Where the arrow stops, it bursts. */
  burstingArrow: { radius: 2000, damageBp: 5000 },
  /** The light also heals the allies under it (bp of their life). */
  mercifulDawn: { hpBp: 2000 },
  /** Those it doesn't fell are left blind (they deal blindBp of their blows for ticks). */
  blindingDawn: { ticks: 48, blindBp: 5000 },
  /** The allies within the rampart also take less. */
  sacredRampart: { takenBp: 8000 },
  /** A rampart that lasts half again as long. */
  longRampart: { durationBp: 15000 },
  /** One heavy shell: harder, wider, and it stuns. */
  heavyMortar: { damageBp: 25000, radiusBp: 13000, stun: 18 },
  /** Each shell leaves the ground burning. */
  incendiaryMortar: { radius: 1200, damage: 10, every: 6, ticks: 36 },
  /** A stronger poison. */
  virulentPlague: { damageBp: 15000 },
  /** The cloud also slows. */
  witheringPlague: { slowBp: 3500 },
  /** A heavier chain: harder, and it holds longer. */
  heavyChain: { damageBp: 20000, stunBp: 15000 },
  /** The chain also drags the two nearest to the target. */
  twinChain: { extra: 2, radius: 1500 },
  /** A deeper fix. */
  deepRepair: { amountBp: 16000 },
  /** It also fixes the two next most damaged nearby, at half. */
  wideRepair: { extra: 2, bp: 5000 },
  /** A longer lance. */
  longLance: { castRange: 9000 },
  /** A lance that bites harder. */
  bitingLance: { damageBp: 16000 },
  /** A heavier arrow. */
  heavyShot: { damageBp: 15000 },
  /** What it goes through is pinned a moment. */
  pinShot: { stun: 12 },
  /** A longer blessing. */
  longRenew: { durationBp: 15000 },
  /** The blessing spreads to the allies around the target. */
  sharedRenew: { radius: 1500 },
  /** Against a nightmare below `below` bp of its life, it hits `bonusBp` as hard. */
  executeBlow: { below: 3000, bonusBp: 20000 },
  /** The blow stuns. */
  stunBlow: { stun: 18 },
  /** A brighter seal: heals more. */
  brightSeal: { healBp: 7000 },
  /** The seal burns its nightmare with light every `every` ticks. */
  burningSeal: { damage: 6, every: 12 },
  /** A longer lash. */
  longLash: { radiusBp: 14000 },
  /** Deeper thorns: it bleeds harder. */
  deepThorns: { bleedBp: 16000 },
  /** A wider shout. */
  wideShout: { radiusBp: 14000 },
  /** A longer stun. */
  longShout: { stunBp: 15000 },
  /** The coil arcs to two more. */
  bigCoil: { extra: 2 },
  /** The coil arcs faster. */
  fastCoil: { every: 8 },
  /** The burst left behind is frost and slows. */
  frostBlink: { slowBp: 5000, slowTicks: 36 },
  /** A longer jump. */
  farBlink: { castRange: 6500 },
  /** The snare leaves its catch bleeding. */
  barbedSnare: { damage: 5, every: 6, ticks: 36 },
  /** Two snares side by side. */
  twinSnare: { gap: 900 },
  /** A deeper prayer: it heals more. */
  deepPrayer: { amountBp: 15000 },
  /** The prayer also wards (allies take takenBp) for ticks. */
  wardPrayer: { takenBp: 8000, ticks: 36 },
  /** A longer bloodletting. */
  longLetting: { durationBp: 15000 },
  /** While it lasts, the barbarian drinks back part of what it deals. */
  thirstyLetting: { lifestealBp: 2500 },
  /** The ally takes even less. */
  steadfast: { takenBp: 3000 },
  /** The leap also heals the ally (bp of its life). */
  healingStep: { hpBp: 1500 },
  /** A longer wall. */
  longThorns: { extra: 2 },
  /** Sharper thorns: they prick harder. */
  sharpThorns: { damageBp: 20000 },
  /** The allies around the warrior can't fall either, for half as long. */
  sharedStand: { durationBp: 5000 },
  /** It lasts half again as long. */
  longStand: { durationBp: 15000 },
  /** A sturdier hound. */
  ironHound: { hpBp: 16000 },
  /** Two hounds, each with part of the life. */
  packHound: { count: 2, hpBp: 6000 },
  /** The meteors fall faster. */
  emberStorm: { every: 3 },
  /** Bigger meteors that hit harder. */
  greatMeteors: { blastBp: 14000, damageBp: 13000 },
  /** It lasts half again as long. */
  longEye: { durationBp: 15000 },
  /** Every shot is a critical while it lasts, not only on elites and bosses. */
  keenEye: { all: true },
  /** It watches half again as long. */
  longIntercession: { durationBp: 15000 },
  /** The ally gets back up with all its life. */
  fullIntercession: { amount: 10000 },
  /** It lasts half again as long. */
  longFury: { durationBp: 15000 },
  /** The blows reach further around. */
  wideFury: { radiusBp: 15000 },
  /** The light lands harder around. */
  radiantAvatar: { amount: 8000 },
  /** The allies near the avatar take even less. */
  guardianAvatar: { takenBp: 5000 },
  /** One ancient tree, much stronger. */
  ancientTreant: { count: 1, hpBp: 25000, damageBp: 20000 },
  /** Three smaller trees. */
  threeTreants: { count: 3, hpBp: 7000 },
  /** A wider slam. */
  wideSlam: { coneDeg: 150, radiusBp: 12000 },
  /** A harder slam that staggers longer. */
  hardSlam: { damageBp: 16000, stun: 18 },
  /** The warrior takes even less behind the wall. */
  bulwarkWall: { selfBp: 3000 },
  /** The wall covers allies further away. */
  wideWall: { radiusBp: 16000 },
  /** A thundering echo: it hits much harder. */
  thunderEcho: { damageBp: 25000 },
  /** A longer stun. */
  longEcho: { stunBp: 15000 },
  /** A longer flame. */
  longFlame: { radiusBp: 14000 },
  /** It burns twice as long and hard. */
  napalm: { burnBp: 20000 },
  /** Two more plates. */
  longBarricade: { extra: 2 },
  /** Spikes on the plates: they hurt what crosses (damage every `every`). */
  spikedBarricade: { damage: 10, every: 6 },
  /** It also mends each structure at once (bp of its life). */
  reinforce: { hpBp: 1500 },
  /** It lasts half again as long. */
  longFortify: { durationBp: 15000 },
  /** The acid eats twice as long. */
  corrosiveDart: { durationBp: 20000 },
  /** The dart splashes the nightmares around too. */
  splashDart: { radius: 1200 },
  /** Five bound instead of three. */
  widerChains: { extra: 2 },
  /** The blows passed along hurt more. */
  heavyChains: { shareBp: 7000 },
  /** A deeper rift: its burst hurts more. */
  deepRift: { damageBp: 15000 },
  /** A wider rift. */
  wideRift: { radiusBp: 14000 },
  /** A longer leap. */
  farLeap: { castRange: 3500 },
  /** Sharper caltrops. */
  sharpCaltrops: { damageBp: 20000 },
  /** A brighter flare: wider. */
  brightFlare: { radiusBp: 15000 },
  /** The flare bursts in fire where it falls. */
  burningFlare: { damage: 40 },
  /** It lasts half again as long. */
  longHidden: { durationBp: 15000 },
  /** Deadlier shots. */
  deadlyHidden: { damageBp: 25000 },
  /** A brighter lance of light: it hurts more. */
  brightLance: { damageBp: 16000 },
  /** It heals more. */
  mercyLight: { healBp: 18000 },
  /** A wider cleansing. */
  wideCleanse: { radiusBp: 16000 },
  /** The cleansed also take less for a while. */
  wardCleanse: { takenBp: 7000, ticks: 36 },
  /** It lasts half again as long. */
  longChoir: { durationBp: 15000 },
  /** A louder choir: it heals more of each blow. */
  loudChoir: { amount: 4000 },
  /** A longer throw. */
  farKick: { push: 3500 },
  /** A harder kick. */
  bruteKick: { damageBp: 20000 },
  /** It reaches further. */
  wideTerrify: { radiusBp: 14000 },
  /** They take even more while cowering. */
  deepTerrify: { markBp: 13000 },
  /** It lasts half again as long. */
  longDuel: { durationBp: 15000 },
  /** The barbarian drinks back part of what it deals its duelist. */
  bloodDuel: { lifestealBp: 3000 },
  /** It heals more. */
  greaterAtone: { amount: 20000 },
  /** It heals every ally around, at half. */
  sharedAtone: { radius: 2500, bp: 5000 },
  /** It lasts half again as long. */
  longBlessing: { durationBp: 15000 },
  /** A brighter blessing: harder blows. */
  brightBlessing: { amount: 14000 },
  /** It lasts half again as long. */
  longSacrifice: { durationBp: 15000 },
  /** The paladin takes less of what it shoulders. */
  wardedSacrifice: { takenBp: 7000 },
  /** A thicker cloud: wider. */
  thickSpores: { radiusBp: 14000 },
  /** A stronger poison. */
  venomSpores: { damageBp: 16000 },
  /** It lasts half again as long. */
  longWolf: { durationBp: 15000 },
  /** A faster wolf. */
  swiftWolf: { amount: 17000 },
  /** The broken ground lasts longer. */
  longQuake: { durationBp: 15000 },
  /** It throws harder. */
  shatterQuake: { damageBp: 25000 },
} as const;

/**
 * The tomes (docs/PODERES.md §6): a slot's tome skill is learned with `pages` pages, no points or level. Each
 * win in that slot's mode gives pages: crypts the short slot's (the crypt in embers and the deep crypt
 * `cryptHard`), incursions the medium's, Marea bosses and saga chapters the ultimate's; a group expedition
 * gives `expedition` to the tome furthest behind.
 */
export const TOMES = { pages: 5, crypt: 1, cryptHard: 2, incursion: 1, boss: 1, saga: 1, expedition: 1 };

const perk = (p: BastionPerk, bpPerRank: number): NodeEffect => ({ kind: 'bastion', perk: p, bpPerRank });

const variants = (cls: HeroClass, slot: 0 | 1 | 2, skill: SkillId, forks: [ForkId, ForkId], tier: number): SkillNode[] =>
  forks.map((fork, i): SkillNode => ({ id: `${cls}.${skill}.${fork}`, cls, branch: slot, tier: tier + i, maxRank: 1, effect: { kind: 'variant', skill, fork } }));

/** A slot's branch: its skill's two variants, then each skill learned there (from its level) with its own two. */
const skillBranch = (cls: HeroClass, slot: 0 | 1 | 2, skill: SkillId, forks: [ForkId, ForkId], learned: [SkillId, number, [ForkId, ForkId]][] = [], tome?: [SkillId, [ForkId, ForkId]]): SkillNode[] => [
  ...variants(cls, slot, skill, forks, 1),
  ...learned.flatMap(([id, level, f], i): SkillNode[] => [
    { id: `${cls}.${id}`, cls, branch: slot, tier: 3 + i * 3, maxRank: 1, effect: { kind: 'learn', skill: id, level } },
    ...variants(cls, slot, id, f, 4 + i * 3),
  ]),
  // The tome's skill comes last: its pages teach it, its variants cost a point each.
  ...(tome
    ? [
        { id: `${cls}.${tome[0]}`, cls, branch: slot, tier: 3 + learned.length * 3, maxRank: 1, effect: { kind: 'learn', skill: tome[0], level: 0, tome: true } } satisfies SkillNode,
        ...variants(cls, slot, tome[0], tome[1], 4 + learned.length * 3),
      ]
    : []),
];

/** The bastion branch: four perks, two ranks each. */
const bastionBranch = (cls: HeroClass, perks: [BastionPerk, number][]): SkillNode[] =>
  perks.map(([p, bp], i) => ({ id: `${cls}.bastion.${i + 1}`, cls, branch: 3, tier: i + 1, maxRank: 2, effect: perk(p, bp) }));

export const SKILL_TREES: Record<HeroClass, SkillNode[]> = {
  warrior: [
    ...skillBranch('warrior', 0, 'shieldBash', ['bashCharge', 'bashCleave'], [['lowBlow', 3, ['crippleBlow', 'exposeBlow']], ['shieldSlam', 14, ['wideSlam', 'hardSlam']]], ['grapple', ['heavyChain', 'twinChain']]),
    ...skillBranch('warrior', 1, 'challenge', ['ironWall', 'warCry'], [['decoy', 6, ['spikedDecoy', 'twinDecoy']], ['shieldWall', 18, ['bulwarkWall', 'wideWall']]], ['defyShout', ['wideShout', 'longShout']]),
    ...skillBranch('warrior', 2, 'oath', ['sharedOath', 'secondWind'], [['guardBanner', 10, ['rallyBanner', 'wideBanner']], ['shieldEcho', 22, ['thunderEcho', 'longEcho']]], ['lastStand', ['sharedStand', 'longStand']]),
    ...bastionBranch('warrior', [['workBonus', 1500], ['repairDiscount', 1000], ['ghostPower', 800], ['incursionLoot', 1000]]),
  ],
  artificer: [
    ...skillBranch('artificer', 0, 'turret', ['frostTurret', 'fireTurret'], [['spikeMine', 3, ['clusterMine', 'tarMine']], ['flamethrower', 14, ['longFlame', 'napalm']]], ['tuneUp', ['deepRepair', 'wideRepair']]),
    ...skillBranch('artificer', 1, 'gearGrenade', ['shrapnel', 'magnet'], [['repairDrone', 6, ['welderDrone', 'armorDrone']], ['ironBarricade', 18, ['longBarricade', 'spikedBarricade']]], ['teslaCoil', ['bigCoil', 'fastCoil']]),
    ...skillBranch('artificer', 2, 'automaton', ['colossus', 'swarm'], [['fieldMortar', 10, ['heavyMortar', 'incendiaryMortar']], ['fortifyAll', 22, ['reinforce', 'longFortify']]], ['clockHound', ['ironHound', 'packHound']]),
    ...bastionBranch('artificer', [['workBonus', 2000], ['repairDiscount', 1500], ['ghostPower', 800], ['incursionLoot', 1000]]),
  ],
  mage: [
    ...skillBranch('mage', 0, 'fireball', ['meteor', 'embers'], [['chainLightning', 3, ['longArc', 'overcharge']], ['acidDart', 14, ['corrosiveDart', 'splashDart']]], ['frostLance', ['longLance', 'bitingLance']]),
    ...skillBranch('mage', 1, 'frostWall', ['longWall', 'bitingFrost'], [['frostOrb', 6, ['orbBurst', 'deepOrb']], ['shadowChains', 18, ['widerChains', 'heavyChains']]], ['blink', ['frostBlink', 'farBlink']]),
    ...skillBranch('mage', 2, 'storm', ['stillStorm', 'thunderclap'], [['blizzard', 10, ['whiteout', 'deepFreeze']], ['dreamRift', 22, ['deepRift', 'wideRift']]], ['fireRain', ['emberStorm', 'greatMeteors']]),
    ...bastionBranch('mage', [['workBonus', 1500], ['repairDiscount', 1000], ['ghostPower', 800], ['incursionLoot', 1000]]),
  ],
  ranger: [
    ...skillBranch('ranger', 0, 'multishot', ['volley', 'frostArrows'], [['poisonArrow', 3, ['piercingVenom', 'virulentArrow']], ['disengage', 14, ['farLeap', 'sharpCaltrops']]], ['piercingShot', ['heavyShot', 'pinShot']]),
    ...skillBranch('ranger', 1, 'net', ['wideNet', 'steelNet'], [['hawk', 6, ['talons', 'hawkEye']], ['signalFlare', 18, ['brightFlare', 'burningFlare']]], ['snare', ['barbedSnare', 'twinSnare']]),
    ...skillBranch('ranger', 2, 'arrowRain', ['pinningRain', 'wideRain'], [['titanArrow', 10, ['pinningArrow', 'burstingArrow']], ['hiddenShot', 22, ['longHidden', 'deadlyHidden']]], ['eagleEye', ['longEye', 'keenEye']]),
    ...bastionBranch('ranger', [['incursionLoot', 1200], ['workBonus', 1500], ['ghostPower', 800], ['repairDiscount', 1000]]),
  ],
  cleric: [
    ...skillBranch('cleric', 0, 'mend', ['mendChain', 'mendWard'], [['faithRay', 3, ['brightRay', 'mendingRay']], ['piercingLight', 14, ['brightLance', 'mercyLight']]], ['renew', ['longRenew', 'sharedRenew']]),
    ...skillBranch('cleric', 1, 'sanctuary', ['consecrate', 'bulwark'], [['guardTotem', 6, ['mendingTotem', 'lightTotem']], ['cleanse', 18, ['wideCleanse', 'wardCleanse']]], ['sharedPrayer', ['deepPrayer', 'wardPrayer']]),
    ...skillBranch('cleric', 2, 'dawn', ['dawnRebirth', 'dawnSmite'], [['dawnJudgment', 10, ['mercifulDawn', 'blindingDawn']], ['choir', 22, ['longChoir', 'loudChoir']]], ['intercession', ['longIntercession', 'fullIntercession']]),
    ...bastionBranch('cleric', [['ghostPower', 1000], ['workBonus', 1500], ['repairDiscount', 1000], ['incursionLoot', 1000]]),
  ],
  barbarian: [
    ...skillBranch('barbarian', 0, 'whirl', ['whirlwind', 'rend'], [['throwingAxe', 3, ['bleedingAxe', 'cleavingAxe']], ['kick', 14, ['farKick', 'bruteKick']]], ['brutalBlow', ['executeBlow', 'stunBlow']]),
    ...skillBranch('barbarian', 1, 'leap', ['quake', 'doubleLeap'], [['charge', 6, ['longCharge', 'crushingCharge']], ['terrify', 18, ['wideTerrify', 'deepTerrify']]], ['bloodletting', ['longLetting', 'thirstyLetting']]),
    ...skillBranch('barbarian', 2, 'frenzy', ['bloodlust', 'warpath'], [['clanRoar', 10, ['dreadRoar', 'longRoar']], ['duel', 22, ['longDuel', 'bloodDuel']]], ['ancestralFury', ['longFury', 'wideFury']]),
    ...bastionBranch('barbarian', [['workBonus', 2000], ['incursionLoot', 1000], ['ghostPower', 800], ['repairDiscount', 1000]]),
  ],
  paladin: [
    ...skillBranch('paladin', 0, 'smite', ['judgment', 'healingLight'], [['blindingLight', 3, ['searingLight', 'wideLight']], ['atone', 14, ['greaterAtone', 'sharedAtone']]], ['holySeal', ['brightSeal', 'burningSeal']]),
    ...skillBranch('paladin', 1, 'aegis', ['reprisal', 'faithWall'], [['spinHammer', 6, ['stunHammer', 'wideHammer']], ['blessWeapon', 18, ['longBlessing', 'brightBlessing']]], ['intervene', ['steadfast', 'healingStep']]),
    ...skillBranch('paladin', 2, 'hallow', ['holyGround', 'faithWeight'], [['holyRampart', 10, ['sacredRampart', 'longRampart']], ['sacrifice', 22, ['longSacrifice', 'wardedSacrifice']]], ['avatarOfLight', ['radiantAvatar', 'guardianAvatar']]),
    ...bastionBranch('paladin', [['repairDiscount', 1500], ['workBonus', 1500], ['ghostPower', 800], ['incursionLoot', 1000]]),
  ],
  druid: [
    ...skillBranch('druid', 0, 'entangle', ['strangle', 'spores'], [['wasps', 3, ['jumpingWasps', 'denseWasps']], ['sporeCloud', 14, ['thickSpores', 'venomSpores']]], ['briarLash', ['longLash', 'deepThorns']]),
    ...skillBranch('druid', 1, 'bearForm', ['direBear', 'wildHeart'], [['deepRoot', 6, ['barkskin', 'spreadingRoots']], ['wolfForm', 18, ['longWolf', 'swiftWolf']]], ['thornWall', ['longThorns', 'sharpThorns']]),
    ...skillBranch('druid', 2, 'bloom', ['deepGrove', 'briars'], [['plague', 10, ['virulentPlague', 'witheringPlague']], ['rootQuake', 22, ['longQuake', 'shatterQuake']]], ['forestAwakening', ['ancientTreant', 'threeTreants']]),
    ...bastionBranch('druid', [['incursionLoot', 1200], ['workBonus', 1500], ['ghostPower', 800], ['repairDiscount', 1000]]),
  ],
};

export const SKILL_NODES: Record<string, SkillNode> = Object.fromEntries(
  Object.values(SKILL_TREES)
    .flat()
    .map((n) => [n.id, n]),
);

/**
 * The damage type a skill looks like (its colour on the buttons and in the field: the hero's weapon glows
 * with its ultimate's); a variant can change it.
 */
export const SKILL_LOOK: Record<SkillId, SkillLook> = {
  shieldBash: 'phys', challenge: 'phys', oath: 'holy', turret: 'phys', gearGrenade: 'phys', automaton: 'bolt',
  fireball: 'fire', frostWall: 'frost', storm: 'bolt', multishot: 'phys', net: 'phys', arrowRain: 'phys',
  mend: 'holy', sanctuary: 'holy', dawn: 'holy', whirl: 'phys', leap: 'phys', frenzy: 'fire',
  smite: 'holy', aegis: 'holy', hallow: 'holy', entangle: 'poison', bearForm: 'phys', bloom: 'poison',
  decoy: 'phys', frostOrb: 'frost', hawk: 'phys', guardTotem: 'holy', spinHammer: 'holy', clanRoar: 'fire', repairDrone: 'bolt', wasps: 'poison',
  lowBlow: 'phys', chainLightning: 'bolt', poisonArrow: 'poison', faithRay: 'holy', throwingAxe: 'phys', blindingLight: 'holy', spikeMine: 'phys', charge: 'phys', deepRoot: 'poison',
  grapple: 'phys', tuneUp: 'phys', frostLance: 'frost', piercingShot: 'phys', renew: 'holy', brutalBlow: 'phys', holySeal: 'holy', briarLash: 'poison',
  defyShout: 'phys', teslaCoil: 'bolt', blink: 'fire', snare: 'phys', sharedPrayer: 'holy', bloodletting: 'phys', intervene: 'holy', thornWall: 'poison',
  lastStand: 'phys', clockHound: 'phys', fireRain: 'fire', eagleEye: 'phys', intercession: 'holy', ancestralFury: 'fire', avatarOfLight: 'holy', forestAwakening: 'poison',
  shieldSlam: 'phys', shieldWall: 'phys', shieldEcho: 'phys', flamethrower: 'fire', ironBarricade: 'phys', fortifyAll: 'phys', acidDart: 'poison', shadowChains: 'bolt', dreamRift: 'bolt', disengage: 'phys', signalFlare: 'fire', hiddenShot: 'phys', piercingLight: 'holy', cleanse: 'holy', choir: 'holy', kick: 'phys', terrify: 'phys', duel: 'fire', atone: 'holy', blessWeapon: 'holy', sacrifice: 'holy', sporeCloud: 'poison', wolfForm: 'phys', rootQuake: 'poison',
  guardBanner: 'phys', blizzard: 'frost', titanArrow: 'phys', dawnJudgment: 'holy', holyRampart: 'holy', fieldMortar: 'fire', plague: 'poison',
};
const VARIANT_LOOK: Partial<Record<ForkId, SkillLook>> = { frostBlink: 'frost', frostTurret: 'frost', fireTurret: 'fire', embers: 'fire', frostArrows: 'frost', consecrate: 'holy' };

export type SkillLook = 'phys' | 'fire' | 'frost' | 'bolt' | 'poison' | 'holy';
/** The look of each damage type a conversion glyph turns a skill into (docs/GLIFOS.md §1). */
const TURNED_LOOK: Partial<Record<DamageType, SkillLook>> = { fire: 'fire', frost: 'frost', lightning: 'bolt', poison: 'poison' };
/** A skill's look as carried: a conversion glyph's type (`turned`, from `glyphTurns`) first, then its variant's, then its own. */
export const lookOf = (skill: SkillId, variant: ForkId | null, turned: DamageType | null = null): SkillLook =>
  (turned && TURNED_LOOK[turned]) || (variant && VARIANT_LOOK[variant]) || SKILL_LOOK[skill];
